const ACCESS_TOKEN_KEY = "access_token";
const REFRESH_TOKEN_KEY = "refresh_token";
const ORG_ID_KEY = "org_id";

let refreshPromise: Promise<string | null> | null = null;

function isBrowser(): boolean {
  return typeof window !== "undefined";
}

export function redirectToLogin(): void {
  if (!isBrowser()) return;
  if (window.location.pathname === "/login") return;
  window.location.href = "/login";
}

function decodeTokenPayload(token: string): Record<string, unknown> | null {
  try {
    return JSON.parse(atob(token.split(".")[1]));
  } catch {
    return null;
  }
}

export function saveTokens(accessToken: string, refreshToken: string) {
  if (!isBrowser()) return;
  sessionStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  const payload = decodeTokenPayload(accessToken);
  if (payload?.org_id) {
    sessionStorage.setItem(ORG_ID_KEY, String(payload.org_id));
  }
}

export function getAccessToken(): string | null {
  if (!isBrowser()) return null;
  return sessionStorage.getItem(ACCESS_TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  if (!isBrowser()) return null;
  return sessionStorage.getItem(REFRESH_TOKEN_KEY);
}

export function getOrgId(): string | null {
  if (!isBrowser()) return null;
  return sessionStorage.getItem(ORG_ID_KEY);
}

export function getUserId(): string | null {
  if (!isBrowser()) return null;
  const token = getAccessToken();
  if (!token) return null;
  const payload = decodeTokenPayload(token);
  return payload?.sub ? String(payload.sub) : null;
}

export function clearTokens() {
  if (!isBrowser()) return;
  sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  sessionStorage.removeItem(ORG_ID_KEY);
}

export function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const exp = payload.exp as number;
    if (!exp) return true;
    return Date.now() >= exp * 1000;
  } catch {
    return true;
  }
}

export async function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken || isTokenExpired(refreshToken)) {
      clearTokens();
      redirectToLogin();
      return null;
    }

    const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000";

    try {
      const response = await fetch(`${API_BASE_URL}/v1/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        clearTokens();
        redirectToLogin();
        return null;
      }

      const data = await response.json();
      saveTokens(data.access_token, data.refresh_token);
      return data.access_token as string;
    } catch {
      clearTokens();
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}